import { Component, Input, OnInit } from '@angular/core';
import {
  EOrderStatus,
  EQuoteRequestVersionStatus,
  EScenarioType,
  IOrderStatusScenario,
  IQuoteRequestVersionStatusScenario,
  OrderStatus,
} from '../../configurations/order-status';
import { EStoreTypes, ETranslationTypes } from '../../configurations/common';
import { environment } from '../../../environments/environment';
import { AppUtils } from '../../utils/app.utils';
import { StringUtils } from '../../utils/string.utils';
import { IPayments } from '../../models/order.models';
import { EPaymentProviderName } from '../../models/checkout.models';

@Component({
  selector: 'app-steps-bar',
  templateUrl: './steps-bar.component.html',
  styleUrls: ['./steps-bar.component.scss'],
  standalone: false,
})
export class StepsBarComponent implements OnInit {
  activeScenario!: IOrderStatusScenario;
  completedSteps: string[] = [];
  eTranslationTypes: typeof ETranslationTypes = ETranslationTypes;
  declinedStatues: string[] = environment.statuesTreatedAsDeclined;
  cpqOrderDeclinedStatus: string = EOrderStatus.ISSUE_WITH_ORDER;

  @Input() comment!: string;
  @Input() scenarioType: EScenarioType = EScenarioType.ORDERS;
  @Input() currentStatus!: string;
  @Input() currentVersionStatus!: string;
  @Input() currentDesc!: string;
  @Input() payment!: IPayments;
  @Input() isRequestQuoteClosedLoopEnabled: boolean = false;
  @Input() isPredefinedEquipmentFlowEnabled: boolean = false;

  ngOnInit(): void {
    switch (this.scenarioType) {
      case EScenarioType.REQUEST:
        this.requestScenarios();
        break;
      case EScenarioType.GENERATED_QUOTE:
        this.generatedQuoteScenarios();
        break;
      case EScenarioType.CPQ:
        this.cpqScenarios();
        this.selectCpqCompletedSteps();
        return;
      case EScenarioType.SPARE_PART_ORDER_DETAILS:
        this.sparePartOrderScenarios();
        return;
      default:
        this.defaultScenarios();
    }

    this.selectCompletedSteps();
  }

  sparePartOrderScenarios(): void {
    if (this.currentStatus === EOrderStatus.IN_PROCESS) {
      this.currentStatus = EOrderStatus.CREATED;
    }

    if (this.currentStatus === EOrderStatus.CALL_SAP) {
      this.currentStatus = EOrderStatus.NEW;
    }

    switch (this.currentStatus) {
      case EOrderStatus.NEW:
      case EOrderStatus.CREATED:
      case EOrderStatus.SHIPPED:
        this.activeScenario = OrderStatus.ShippedSparePartOrderScenario;
        break;
      case EOrderStatus.PARTIALLY_SHIPPED:
        this.activeScenario = OrderStatus.PartiallyShippedSparePartOrderScenario;
        break;
      case EOrderStatus.CANCELED:
        this.activeScenario = OrderStatus.CanceledSparePartOrderScenario;
        break;
      case EOrderStatus.ISSUE:
        this.activeScenario = OrderStatus.IssueSparePartOrderScenario;
        break;
      case EOrderStatus.SEE_DETAILS:
        this.activeScenario = OrderStatus.SeeDetailsSparePartOrderScenario;
        break;
      default:
        this.activeScenario = OrderStatus.DefaultOrderScenario;
        break;
    }
  }

  selectCompletedSteps(): void {
    const status: string = this.isRequestQuoteClosedLoopEnabled && this.currentVersionStatus
      ? this.currentVersionStatus
      : this.currentStatus;

    this.activeScenario.scenarioSteps.every((element) => {
      this.completedSteps.push(element);
      return element !== status;
    });
  }

  selectCpqCompletedSteps(): void {
    if (this.currentStatus === EOrderStatus.CPQ_ORDER_DECLINED) {
      this.completedSteps = this.activeScenario.scenarioSteps;
    } else {
      let currentStatusIndex: number = 0;
      this.activeScenario.scenarioSteps.forEach((element, index) => {
        this.completedSteps.push(element);
        if (element === this.currentStatus) {
          currentStatusIndex = index;
        }
      });
      this.completedSteps = this.completedSteps.slice(0, currentStatusIndex);
    }
  }

  requestScenarios(): void {
    if (this.currentStatus === EOrderStatus.CANCELED && !this.currentVersionStatus) {
      this.activeScenario = OrderStatus.RequestCanceledScenario;
      return;
    }

    if (!this.isRequestQuoteClosedLoopEnabled) {
      this.activeScenario = OrderStatus.RequestSuccessScenario;
      return;
    }

    const statusScenarioMap: Record<EQuoteRequestVersionStatus, IQuoteRequestVersionStatusScenario | null> = {
      [EQuoteRequestVersionStatus.ACCEPTED]: OrderStatus.RequestVersionAcceptedScenario,
      [EQuoteRequestVersionStatus.CANCELED]: OrderStatus.RequestVersionCanceledScenario,
      [EQuoteRequestVersionStatus.EMPTY]: null,
      [EQuoteRequestVersionStatus.EXPIRED]: OrderStatus.RequestVersionExpiredScenario,
      [EQuoteRequestVersionStatus.NOT_ORDERED]: OrderStatus.RequestVersionNotOrderedScenario,
      [EQuoteRequestVersionStatus.ORDERED]: OrderStatus.RequestVersionOrderedScenario,
      [EQuoteRequestVersionStatus.OUTDATED]: OrderStatus.RequestVersionOutdatedScenario,
      [EQuoteRequestVersionStatus.PENDING]: OrderStatus.RequestVersionPendingScenario,
      [EQuoteRequestVersionStatus.REJECTED]: OrderStatus.RequestVersionRejectedScenario,
      [EQuoteRequestVersionStatus.REQUESTED]: OrderStatus.RequestVersionRequestedScenario,
    };
    this.activeScenario = (statusScenarioMap[this.currentVersionStatus as EQuoteRequestVersionStatus] ?? OrderStatus.RequestVersionRequestedScenario) as unknown as IOrderStatusScenario;
  }

  generatedQuoteScenarios(): void {
    if (this.currentStatus === EOrderStatus.CANCELED) {
      this.activeScenario = OrderStatus.GeneratedQuoteCanceledScenario;
    } else {
      this.activeScenario = OrderStatus.GeneratedQuoteSuccessScenario;
    }
  }

  cpqScenarios(): void {
    switch (this.currentStatus) {
      case EOrderStatus.ORDER_SUBMITTED:
        this.activeScenario = OrderStatus.CpqPendingScenario;
        break;
      case EOrderStatus.PENDING_DOCUMENTATION:
        this.activeScenario = OrderStatus.CpqPendingScenario;
        break;
      case EOrderStatus.DOCUMENTATION_SUBMITTED:
        this.activeScenario = OrderStatus.CpqReviewScenario;
        break;
      case EOrderStatus.ISSUE_WITH_ORDER:
        this.activeScenario = OrderStatus.CpqIssueWithOrderScenario;
        break;
      case EOrderStatus.UNDER_REVIEW:
        this.activeScenario = OrderStatus.CpqUnderReviewScenario;
        break;
      case EOrderStatus.DOCUMENT_ACCEPTED:
      case EOrderStatus.ORDER_COMPLETE:
        this.activeScenario = OrderStatus.CpqOrderCompleteScenario;
        break;
      case EOrderStatus.CPQ_ORDER_DECLINED:
        this.activeScenario = OrderStatus.CpqOrderDeclinedScenario;
        break;
      default:
        break;
    }
  }

  defaultScenarios(): void {
    switch (this.currentStatus) {
      case EOrderStatus.DECLINED:
        this.activeScenario = OrderStatus.DeclineScenario;
        break;
      case EOrderStatus.CANCELED:
        this.activeScenario = OrderStatus.CanceledOrderScenario;
        break;
      case EOrderStatus.PAYMENT_FAILED:
        this.activeScenario = OrderStatus.FailedCCAvenuePaymentScenario;
        break;
      case EOrderStatus.PAYMENT_CANCELED:
        this.activeScenario = OrderStatus.CanceledCCAvenuePaymentScenario;
        break;
      default:
        this.setDefaultScenarios();
    }
  }

  private setDefaultScenarios() {
    if (AppUtils.isStoreActive(EStoreTypes.IN)) {
      if (this.payment.paymentProvider === EPaymentProviderName.CC_AVENUE) {
        this._handleCCAvenueScenario();
      } else {
        this.activeScenario = OrderStatus.IndiaSuccessScenario;
      }
    } else if (AppUtils.isStoreActive(EStoreTypes.JP)) {
      this.activeScenario = OrderStatus.JapanSuccessScenario;
    } else if (this.isPredefinedEquipmentFlowEnabled) {
      this._predefinedEquipmentFlowScenario();
    } else {
      this.activeScenario = OrderStatus.SuccessScenario;
    }

    return this.activeScenario;
  }

  private _handleCCAvenueScenario(): void {
    if (this.currentStatus === EOrderStatus.PAYMENT_PENDING) {
      this.currentStatus = EOrderStatus.REQUEST;
    } else if (this.currentStatus === EOrderStatus.PAYMENT_SUCCESSFUL) {
      this.currentStatus = EOrderStatus.PAYMENT_PROCESSED;
    }

    this.activeScenario = OrderStatus.IndiaCCAvenueSuccessScenario;
  }

  private _predefinedEquipmentFlowScenario(): void {
    switch (this.currentStatus) {
      case EOrderStatus.SHIPPED:
      case EOrderStatus.INVOICE_SENT:
        this.currentStatus = EOrderStatus.CONFIRMED;
        break;
    }

    this.activeScenario = OrderStatus.PredefinedEquipmentFlowScenario;
  }

  getTitlePosition(): number {
    const hSteps: string[] | undefined = this.activeScenario.hospitalSteps || this.activeScenario.organizationSteps;
    if (!hSteps || hSteps.length === 0) {
      return 0;
    }
    const middle: string = hSteps[Math.floor(hSteps.length / 2)];
    return hSteps.length % 2 !== 0 ? hSteps.indexOf(middle) : hSteps.indexOf(middle) - 1;
  }

  getStepName(step: string): string {
    return StringUtils.replaceSpaces(step, '-');
  }

  getTranslation(step: string, type: ETranslationTypes): string {
    const stepName: string = this.getStepName(step);
    switch (type) {
      case ETranslationTypes.LABEL:
        return `${this.scenarioType}-step-bar.${stepName}-label`;
      case ETranslationTypes.DESCRIPTION:
        return `${this.scenarioType}-step-bar.${stepName}-desc`;
      default:
        return '';
    }
  }

  getComment(step: string, type: ETranslationTypes): string {
    if (!this.currentDesc || (this.currentDesc && this.currentStatus !== step)) {
      return this.getTranslation(step, type);
    }
    return '';
  }

  /**
   * Returns steps matches the current status.
   * Uses version status when the Request Quote Closed Loop feature is enabled and a version status exists.
   * Otherwise uses the default order status.
   */
  shouldUseVersionStatus(step: EOrderStatus | EQuoteRequestVersionStatus): boolean {
    const status: string = this.isRequestQuoteClosedLoopEnabled && this.currentVersionStatus
      ? this.currentVersionStatus
      : this.currentStatus;
    return step === status;
  }
}
